import { Module, MutationTree, ActionTree, GetterTree } from "vuex";
import { AxiosError } from "axios";
import axios from "@/shared/scripts/AutoRefreshAxios";

import { RootState } from "../../store.interface";
import { Customer, CustomerApiError } from "@/shared/interfaces/customer/Customer.interface";
import { CustomerApiStoreState } from "@/shared/interfaces/customer/CustomerStoreState.interface";
import { cloneDeep } from "lodash";

import {
    applyCustomerNameFallback,
    cleanCustomerObject,
    removeUnexpectedCustomerProperties
} from "@/shared/scripts/helpers/customer/customer";

const DOMAIN: string = process.env.VUE_APP_CUSTOMERS_MANAGEMENT_URL || "";
const API_VERSION = "1";
const RESOURCE = "/customers";

const url = DOMAIN + API_VERSION + RESOURCE;

const state: CustomerApiStoreState = {
    customerApiErrors: [],
};

const mutations: MutationTree<CustomerApiStoreState> = {
    errors: function(state: CustomerApiStoreState, errors: Array<CustomerApiError>) {
        state.customerApiErrors = errors;
    },
};

const actions: ActionTree<CustomerApiStoreState, RootState> = {
    getCustomers: function({ commit, getters }): Promise<boolean> {
        return new Promise((resolve, reject) => {
            axios({
                url: url,
                headers: getters.apiHeaders,
            })
                .then(response => {
                    commit("setCustomers", response.data);
                    resolve(true);
                })
                .catch((e: AxiosError) => reject(e.response?.data.errors));
        });
    },

    setCurrentCustomer: function({ commit, getters }, customerId: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            axios({
                url: `${url}/${customerId}`,
                headers: getters.apiHeaders,
            })
                .then(response => {
                    commit("setCurrentCustomer", response.data);
                    resolve(true);
                })
                .catch((e: AxiosError) => reject(e.response?.data.errors));
        });
    },

    createCustomer: function({ commit, dispatch, getters, rootState }, customer): Promise<boolean> {
        dispatch("resetErrors");

        // Prepare payload

        // Callers hand us live Vuex objects and everything below mutates the payload — including
        // deleting keys, which Vue 2 cannot track. Work on a copy so the store is only ever
        // changed through a mutation.
        customer = cloneDeep(customer);

        applyCustomerNameFallback(
            customer,
            rootState.UserModule.user?.username,
            rootState.UserModule.user?.email
        );

        customer = cleanCustomerObject(customer);

        // Send request and update store.

        return new Promise((resolve, reject) => {
            axios({
                method: "post",
                url: url,
                headers: getters.apiHeaders,
                data: customer,
            })
                .then(response => {
                    const customerId = response.data.id;
                    customer.id = customerId;
                    commit("addCustomer", customer);
                    commit("setCurrentCustomer", customer);
                    commit("addValue", { "path": "id", "value": customerId });
                    commit("setInitialCustomer", customer);
                    commit("addValue", { "path": "id", "value": customerId });
                    resolve(true);
                })
                .catch((e: AxiosError) => {
                    const errors = e.response?.data;
                    // Malformed request
                    if (errors) {
                        commit("errors", errors);
                        resolve(false);
                    }

                    // Network errors
                    if (e.request) {
                        reject(e.request);
                    }

                    // Other error
                    reject(e.message);
                });
        });
    },

    updateCustomer: function({ commit, dispatch, getters, rootState }, customer): Promise<boolean> {
        const customerId = customer.id;

        // Read-only flag, stripped from the payload below. It drives the IO-222/IO-224 billing lock
        // (CustomerForm's isBillingLocked, Street.vue), so it has to survive back onto the stored
        // customer or a paid customer's country/VAT/invoicing fields look editable again and the
        // PUT is rejected on the next save.
        const hasPayment = customer.hasPayment;

        dispatch("resetErrors");

        // Prepare payload

        // Callers hand us live Vuex objects and everything below mutates the payload — including
        // deleting keys, which Vue 2 cannot track. Work on a copy so the store is only ever
        // changed through a mutation.
        customer = cloneDeep(customer);

        removeUnexpectedCustomerProperties(customer);

        applyCustomerNameFallback(
            customer,
            rootState.UserModule.user?.username,
            rootState.UserModule.user?.email
        );

        customer = cleanCustomerObject(customer) as Customer;

        // Send request and update store.

        return new Promise((resolve, reject) => {
            axios({
                method: "put",
                url: `${url}/${customerId}`,
                data: customer,
                headers: getters.apiHeaders,
            })
                .then(() => {
                    // Build the stored record as a copy: `customer` is the object we just handed
                    // to axios, and the payload must not gain the id / read-only flags back.
                    const storedCustomer = { ...customer, id: customerId } as Customer;

                    if (hasPayment !== undefined) {
                        storedCustomer.hasPayment = hasPayment;
                    }

                    commit("setCurrentCustomer", storedCustomer);
                    resolve(true);
                })
                .catch((e: AxiosError) => {
                    const errors = e.response?.data;

                    // Malformed request
                    if (errors) {
                        commit("errors", errors);
                        resolve(false);
                    }

                    // Network errors
                    if (e.request) {
                        reject(e.request);
                    }

                    // Other error
                    reject(e.message);
                });
        });
    },

    resetErrors: function({ commit }) {
        commit("errors", []);
    },
};

const getters: GetterTree<CustomerApiStoreState, RootState> =  {
    apiHeaders: ((state, getters, rootState): Record<string, string> => ({
        "Accept": "application/json",
        "Authorization": "Bearer " + rootState.UserModule.user?.jwtToken,
    })),

    customerApiErrors: (({ customerApiErrors }: CustomerApiStoreState): CustomerApiError[] | null => {
        return customerApiErrors.length > 0 ? customerApiErrors : null;
    }),
};

const api: Module<CustomerApiStoreState, RootState> = {
    state: state,
    mutations: mutations,
    actions: actions,
    getters: getters,
};

export default api;
